import { computed, reactive, ref } from 'vue';
import {
  BundleBuilderOptions,
  BundleBuilderState,
  BundleGroupInfo,
  BundleGroupOptions,
  BundleItem,
  CartPayload,
  CheckoutProductSelection,
  ProductComplete,
  ProductSelection,
  ValidationState,
} from '@/features/Product/types/models';
import { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';
import { useMoneyHelper } from '@/composables/moneyHelpers';
import { useBundlePricing } from '@/features/Product/composables/useBundlePricing';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import { useRandomisation } from '@/features/Product/composables/useRandomisation';
import { resolveBundleCards } from '@/features/Product/composables/bundleCardHelper';

export const useBundleBuilder = (options: BundleBuilderOptions) => {
  const { bundleDefinition, autoSelectDefaults = true, resolvePoolPlaceholder = false } = options;
  const { isProductSoldOut, getExactStock } = useProductStockStore();
  const randomisationPool = resolvePoolPlaceholder
    ? useRandomisation(
        ref(bundleDefinition.additionalBundleDetails?.randomisation),
        undefined,
        undefined,
        ref(resolveBundleCards(bundleDefinition.additionalBundleDetails).map(({ sku }) => sku)),
      )
    : null;

  const isOptionSoldOut = (product: ProductComplete): boolean => {
    if (!randomisationPool?.isPoolPlaceholder(product)) return isProductSoldOut(product);
    return randomisationPool.poolSlotCapacity.value === 0;
  };

  const getOptionStockLimit = (product: ProductComplete): number | undefined => {
    if (!randomisationPool?.isPoolPlaceholder(product)) {
      return getExactStock(product.attributes.variantId);
    }
    const { poolSlotCapacity } = randomisationPool;
    return Number.isFinite(poolSlotCapacity.value) ? poolSlotCapacity.value : undefined;
  };

  const activeGroupKey = ref<string | null>(null);
  const activeGroupIndex = ref(-1);

  const activeGroup = computed(() => {
    return getBundleGroups().find((group) => group.key === activeGroupKey.value);
  });

  const getGroupKey = (item: BundleItem, index: number): string => {
    return item.groupingName || `group${index}`;
  };

  const getQuantitySelectedOutsideGroup = (variantId: number, groupKey: string): number => {
    let total = 0;
    bundleState.selections.forEach((selection, selectionGroupKey) => {
      if (selectionGroupKey === groupKey) return;
      total += selection.products.get(variantId)?.quantity ?? 0;
    });
    return total;
  };

  const getStockMaxForProduct = (
    product: ProductComplete,
    groupKey: string,
  ): number | undefined => {
    const stockLimit = getOptionStockLimit(product);
    if (stockLimit === undefined) return undefined;
    const quantityOutsideGroup = getQuantitySelectedOutsideGroup(
      product.attributes.variantId,
      groupKey,
    );
    return Math.max(0, stockLimit - quantityOutsideGroup);
  };

  const isProductAvailable = (product: ProductComplete, groupKey: string): boolean =>
    !isOptionSoldOut(product) && getStockMaxForProduct(product, groupKey) !== 0;

  const categories = bundleDefinition.additionalBundleDetails?.categories ?? {};

  const getMaxQuantityForProduct = (product: ProductComplete, groupKey: string): number => {
    const defaultMaxCartQuantity = 5;
    const skuMaxQuantity =
      bundleDefinition.additionalBundleDetails?.maxQuantities?.[product.attributes.sku];
    const configuredMax =
      skuMaxQuantity ?? (product.attributes?.maxCartQuantity || defaultMaxCartQuantity);
    const stockMax = getStockMaxForProduct(product, groupKey);
    return stockMax === undefined ? configuredMax : Math.min(configuredMax, stockMax);
  };

  const getCategoryKeyForProduct = (product: ProductComplete): string | undefined =>
    Object.keys(categories).find((key) => categories[key].skus.includes(product.attributes.sku));

  const getCategoryQuantity = (categoryKey: string): number => {
    let total = 0;
    bundleState.selections.forEach((selection) => {
      selection.products.forEach((productSelection) => {
        if (getCategoryKeyForProduct(productSelection.product) === categoryKey) {
          total += productSelection.quantity;
        }
      });
    });
    return total;
  };

  const getEffectiveMaxQuantityForProduct = (
    product: ProductComplete,
    groupKey: string,
    currentQuantity: number,
  ): number => {
    const perProductMax = getMaxQuantityForProduct(product, groupKey);
    const categoryKey = getCategoryKeyForProduct(product);
    if (!categoryKey) return perProductMax;

    const categoryTotalExcludingThis = getCategoryQuantity(categoryKey) - currentQuantity;
    const categoryHeadroom = Math.max(
      0,
      categories[categoryKey].maxQuantity - categoryTotalExcludingThis,
    );
    return Math.min(perProductMax, categoryHeadroom);
  };

  const setActiveGroup = (groupKey: string, groupIndex: number) => {
    activeGroupKey.value = groupKey;
    activeGroupIndex.value = groupIndex;
  };

  const clearActiveGroup = () => {
    activeGroupKey.value = null;
    activeGroupIndex.value = -1;
  };

  const canAddToGroup = (group: BundleGroupInfo) => {
    if (group.maxSelection && group.totalQuantity >= group.maxSelection) {
      return false;
    }
    const bundleMax = bundleDefinition.maxItems;
    if (bundleMax && totalQuantity.value >= bundleMax) return false;
    return true;
  };

  const getGroupSelections = (group: BundleGroupInfo) => {
    return group.options.filter((option) => option.isSelected && option.quantity > 0);
  };

  const bundleState = reactive<BundleBuilderState>({
    selections: new Map(),
    isInitialised: false,
  });

  const findBundleItem = (groupKey: string): BundleItem | undefined => {
    return bundleDefinition.bundleItems.find(
      (item, index) => getGroupKey(item, index) === groupKey,
    );
  };

  const initialiseBundle = () => {
    if (bundleState.isInitialised) return;

    bundleDefinition.bundleItems.forEach((item, index) => {
      const groupKey = getGroupKey(item, index);

      if (item.itemType === 'fixed') {
        const productMap = new Map<number, ProductSelection>();
        (item.processedProducts || []).forEach((product) => {
          productMap.set(product.attributes.variantId, {
            product,
            quantity: item.quantity || 1,
          });
        });

        bundleState.selections.set(groupKey, {
          products: productMap,
          totalQuantity: (item.processedProducts?.length || 0) * (item.quantity || 1),
        });
      } else if (item.itemType === 'choice_group' && autoSelectDefaults) {
        const minSelection = item.minSelection || 0;
        const productMap = new Map<number, ProductSelection>();
        const defaultProducts = (item.processedProducts || [])
          .filter((product) => isProductAvailable(product, groupKey))
          .slice(0, minSelection);

        defaultProducts.forEach((product) => {
          productMap.set(product.attributes.variantId, {
            product,
            quantity: 1,
          });
        });

        if (productMap.size > 0) {
          bundleState.selections.set(groupKey, {
            products: productMap,
            totalQuantity: defaultProducts.length,
          });
        }
      }
    });
    bundleState.isInitialised = true;
  };

  const addToGroup = (
    groupKey: string,
    product: ProductComplete,
    quantity: number = 1,
  ): boolean => {
    const bundleItem = findBundleItem(groupKey);
    if (!bundleItem) return false;

    let currentSelection = bundleState.selections.get(groupKey);
    if (!currentSelection) {
      currentSelection = {
        products: new Map(),
        totalQuantity: 0,
      };
      bundleState.selections.set(groupKey, currentSelection);
    }

    const existingSelection = currentSelection.products.get(product.attributes.variantId);
    const currentQuantity = existingSelection?.quantity || 0;
    const newQuantity = currentQuantity + quantity;

    const maxForProduct = getEffectiveMaxQuantityForProduct(product, groupKey, currentQuantity);
    if (newQuantity > maxForProduct) return false;

    if (bundleDefinition.maxItems) {
      const currentBundleTotal = getTotalQuantityAcrossAllGroups();
      const additionalItems = quantity;

      if (currentBundleTotal + additionalItems > bundleDefinition.maxItems) return false;
    }

    if (bundleItem.maxSelection) {
      const currentGroupTotal = currentSelection.totalQuantity;
      const newTotal = currentGroupTotal - currentQuantity + newQuantity;

      if (newTotal > bundleItem.maxSelection) return false;
    }

    if (existingSelection) {
      existingSelection.quantity = newQuantity;
    } else {
      currentSelection.products.set(product.attributes.variantId, {
        product,
        quantity: quantity,
      });
    }

    currentSelection.totalQuantity = Array.from(currentSelection.products.values()).reduce(
      (total, selected) => total + selected.quantity,
      0,
    );

    return true;
  };

  const removeFromGroup = (groupKey: string, variantId: number, quantity: number = 1): boolean => {
    const selection = bundleState.selections.get(groupKey);
    if (!selection) return false;

    const productSelection = selection.products.get(variantId);
    if (!productSelection) return false;

    const newQuantity = productSelection.quantity - quantity;

    if (newQuantity <= 0) {
      selection.products.delete(variantId);
    } else {
      productSelection.quantity = newQuantity;
    }

    selection.totalQuantity = Array.from(selection.products.values()).reduce(
      (total, selected) => total + selected.quantity,
      0,
    );

    if (selection.products.size === 0) {
      bundleState.selections.delete(groupKey);
    }

    return true;
  };

  const setProductQuantity = (
    groupKey: string,
    product: ProductComplete,
    quantity: number,
  ): boolean => {
    if (quantity <= 0) {
      return removeFromGroup(groupKey, product.attributes.variantId, 0);
    }

    const bundleItem = findBundleItem(groupKey);
    if (!bundleItem) return false;

    let currentSelection = bundleState.selections.get(groupKey);
    if (!currentSelection) {
      currentSelection = {
        products: new Map(),
        totalQuantity: 0,
      };
      bundleState.selections.set(groupKey, currentSelection);
    }

    const existingSelection = currentSelection.products.get(product.attributes.variantId);
    const currentQuantity = existingSelection?.quantity || 0;
    const quantityDiff = quantity - currentQuantity;

    const maxForProduct = getEffectiveMaxQuantityForProduct(product, groupKey, currentQuantity);
    if (quantity > maxForProduct) return false;

    if (bundleDefinition.maxItems) {
      const currentBundleTotal = getTotalQuantityAcrossAllGroups();
      if (currentBundleTotal + quantityDiff > bundleDefinition.maxItems) return false;
    }

    if (bundleItem.maxSelection) {
      const newTotal = currentSelection.totalQuantity + quantityDiff;
      if (newTotal > bundleItem.maxSelection) return false;
    }

    currentSelection.products.set(product.attributes.variantId, {
      product,
      quantity,
    });

    currentSelection.totalQuantity = Array.from(currentSelection.products.values()).reduce(
      (total, selected) => total + selected.quantity,
      0,
    );

    return true;
  };

  const removeSoldOutSelections = (): { product: ProductComplete; remainingQuantity: number }[] => {
    const usedQuantities = new Map<number, number>();
    const trimmedSelections: { product: ProductComplete; remainingQuantity: number }[] = [];
    const groups = bundleDefinition.bundleItems.map((item, index) => ({
      item,
      groupKey: getGroupKey(item, index),
    }));

    groups
      .filter(({ item }) => item.itemType === 'fixed')
      .forEach(({ groupKey }) => {
        bundleState.selections.get(groupKey)?.products.forEach(({ quantity }, variantId) => {
          usedQuantities.set(variantId, (usedQuantities.get(variantId) ?? 0) + quantity);
        });
      });

    groups
      .filter(({ item }) => item.itemType === 'choice_group')
      .forEach(({ groupKey }) => {
        const selection = bundleState.selections.get(groupKey);
        if (!selection) return;
        [...selection.products].forEach(([variantId, { product, quantity }]) => {
          const usedQuantity = usedQuantities.get(variantId) ?? 0;
          const exactStock = isOptionSoldOut(product) ? 0 : getOptionStockLimit(product);
          const keptQuantity =
            exactStock === undefined
              ? quantity
              : Math.min(quantity, Math.max(0, exactStock - usedQuantity));
          if (keptQuantity < quantity) {
            removeFromGroup(groupKey, variantId, quantity - keptQuantity);
            trimmedSelections.push({ product, remainingQuantity: keptQuantity });
          }
          usedQuantities.set(variantId, usedQuantity + keptQuantity);
        });
      });
    return trimmedSelections;
  };

  const clearGroup = (groupKey: string) => {
    bundleState.selections.delete(groupKey);
  };

  const getTotalQuantityAcrossAllGroups = (): number => {
    let total = 0;
    bundleState.selections.forEach((selection) => {
      total += selection.totalQuantity;
    });
    return total;
  };

  const getProductQuantity = (groupKey: string, variantId: number): number => {
    const selection = bundleState.selections.get(groupKey);
    if (!selection) return 0;
    return selection.products.get(variantId)?.quantity || 0;
  };

  const selectedProducts = computed<ProductSelection[]>(() => {
    const pricing = bundleDefinition.pricingRules;

    if (
      pricing.type === 'first_x_paid' ||
      pricing.type === 'first_x_free' ||
      pricing.type === 'sum'
    ) {
      // Selection order determines which items are paid/free for first_x_paid and first_x_free bundles
      const allItems: { product: ProductComplete; originalPrice: number }[] = [];

      bundleState.selections.forEach((selection) => {
        selection.products.forEach((productSelection) => {
          for (let i = 0; i < productSelection.quantity; i++) {
            allItems.push({
              product: productSelection.product,
              originalPrice: productSelection.product.attributes.price,
            });
          }
        });
      });

      const paidCount = pricing.paidItemsCount || allItems.length;
      const freeCount = pricing.freeItemsCount || 1;

      return allItems.reduce((groupedProducts, current, index) => {
        let price = current.originalPrice;

        if (pricing.type === 'first_x_paid' && index >= paidCount) {
          price = 0;
        }

        if (pricing.type === 'first_x_free' && index < freeCount) {
          price = 0;
        }

        if (pricing.type === 'sum') {
          if (pricing.discountPercentage) {
            price = price * (1 - pricing.discountPercentage / 100);
          } else if (pricing.discountAmount) {
            price = price - pricing.discountAmount / allItems.length;
          }
        }

        const priceInDollars = useMoneyHelper(price).dollarValue;

        const existingEntry = groupedProducts.find(
          (entry) =>
            entry.product.attributes.variantId === current.product.attributes.variantId &&
            entry.price === priceInDollars,
        );

        if (existingEntry) {
          existingEntry.quantity += 1;
        } else {
          groupedProducts.push({
            product: current.product,
            quantity: 1,
            price: priceInDollars,
          });
        }

        return groupedProducts;
      }, [] as ProductSelection[]);
    }

    const products: { product: ProductComplete; quantity: number }[] = [];
    bundleState.selections.forEach((selection) => {
      selection.products.forEach((productSelection) => {
        products.push({
          product: productSelection.product,
          quantity: productSelection.quantity,
        });
      });
    });
    return products;
  });

  const totalQuantity = computed<number>(() => {
    return selectedProducts.value.reduce((total, item) => total + item.quantity, 0);
  });

  const bundlePricing = useBundlePricing(bundleDefinition, selectedProducts);

  const totalPrice = computed(() => bundlePricing.currentPrice.value);

  const fromPrice = computed(() => bundlePricing.fromPrice.value);

  const validationState = computed<ValidationState>(() => {
    const errors: string[] = [];
    const warnings: string[] = [];
    let isValid = true;

    const totalSelected = totalQuantity.value;

    if (bundleDefinition.minItems !== undefined) {
      if (totalSelected < bundleDefinition.minItems) {
        errors.push(
          `Please select at least ${bundleDefinition.minItems} items (currently ${totalSelected})`,
        );
        isValid = false;
      }
    }

    if (bundleDefinition.maxItems !== undefined) {
      if (totalSelected > bundleDefinition.maxItems) {
        errors.push(
          `Maximum ${bundleDefinition.maxItems} items allowed (currently ${totalSelected})`,
        );
        isValid = false;
      }
    }

    bundleDefinition.bundleItems.forEach((item, index) => {
      const groupKey = getGroupKey(item, index);
      const selection = bundleState.selections.get(groupKey);
      const groupQuantity = selection?.totalQuantity || 0;

      if (item.isRequired && groupQuantity === 0) {
        errors.push(`Please select items for ${item.groupingName || 'required group'}`);
        isValid = false;
      }

      if (item.minSelection && groupQuantity < item.minSelection) {
        errors.push(
          `Select at least ${item.minSelection} items for ${item.groupingName || groupKey}`,
        );
        isValid = false;
      }

      if (item.maxSelection && groupQuantity > item.maxSelection) {
        warnings.push(
          `Maximum ${item.maxSelection} items allowed for ${item.groupingName || groupKey}`,
        );
      }
    });

    return { isValid, errors, warnings, totalSelected };
  });

  const isValidBundle = computed<boolean>(() => validationState.value.isValid);

  const createCustomBundleDetails = (
    selectedProducts: ProductSelection[],
  ): CheckoutProductSelection[] => {
    if (!selectedProducts) return [];
    return selectedProducts.reduce((checkoutItems, current) => {
      const { product, quantity, price } = current;
      const { attributes } = product;
      const item_details: CheckoutProductSelection = {
        id: `gid://shopify/ProductVariant/${attributes.variantId}`,
        sku: attributes.sku,
        quantity: quantity || 1,
      };

      // Add the first_x_paid or the sum value so price is correct in checkout
      if (price !== undefined) {
        item_details.price = price;
      }

      checkoutItems.push(item_details);
      return checkoutItems;
    }, []);
  };

  const buildCartObject = (parent_product: ProductComplete = null): CartPayload | null => {
    if (!isValidBundle.value || !parent_product) return null;

    const items: AddToCartItem = {
      id: parent_product.attributes.variantId,
      quantity: 1,
      properties: {
        _custom_bundle_details: JSON.stringify(createCustomBundleDetails(selectedProducts.value)),
        _max_cart_quantity: String(parent_product.attributes.maxCartQuantity ?? 5),
      },
    };

    return {
      items,
    };
  };

  const handleGroupSelection = (product: ProductComplete): boolean => {
    if (!activeGroupKey.value || !activeGroup.value) return false;
    if (activeGroup.value.maxSelection === 1) {
      const currentSelection = getGroupSelections(activeGroup.value)[0];
      if (currentSelection) {
        removeFromGroup(activeGroupKey.value, currentSelection.product.attributes.variantId);
      }
      return addToGroup(activeGroupKey.value, product);
    }
    if (activeGroup.value.totalQuantity < activeGroup.value.maxSelection) {
      return addToGroup(activeGroupKey.value, product);
    }
    return false;
  };

  const getGroupOption = (groupKey: string, product: ProductComplete): BundleGroupOptions => {
    const productSelection = bundleState.selections
      .get(groupKey)
      ?.products.get(product.attributes.variantId);
    const quantity = productSelection?.quantity || 0;
    return {
      product,
      quantity,
      isSelected: !!productSelection,
      isAvailable:
        !isOptionSoldOut(product) &&
        (quantity > 0 || getStockMaxForProduct(product, groupKey) !== 0),
      maxQuantity: getEffectiveMaxQuantityForProduct(product, groupKey, quantity),
    };
  };

  const getBundleGroups = (): BundleGroupInfo[] => {
    return bundleDefinition.bundleItems.map((item, index) => {
      const groupKey = getGroupKey(item, index);
      const selection = bundleState.selections.get(groupKey);

      return {
        key: groupKey,
        type: item.itemType,
        title: item.groupingName || `Option ${index + 1}`,
        isRequired: item.isRequired,
        minSelection: item.minSelection,
        maxSelection: item.maxSelection,
        totalQuantity: selection?.totalQuantity || 0,
        isValid: !item.isRequired || (selection?.totalQuantity || 0) >= (item.minSelection || 1),
        options: (item.processedProducts || []).map((product) => getGroupOption(groupKey, product)),
      };
    });
  };

  initialiseBundle();

  return {
    bundleState,
    selectedProducts,
    totalPrice,
    fromPrice,
    totalQuantity,
    isValidBundle,
    validationState,
    bundleDefinition,
    activeGroup,
    activeGroupKey,
    activeGroupIndex,

    addToGroup,
    removeFromGroup,
    setProductQuantity,
    getProductQuantity,
    getGroupOption,
    clearGroup,
    removeSoldOutSelections,
    initialiseBundle,
    buildCartObject,
    getBundleGroups,
    setActiveGroup,
    clearActiveGroup,
    canAddToGroup,
    getGroupSelections,
    handleGroupSelection,
  };
};
